// Calendar shared helpers — used by FullCalendarPage (year/month/week/day),
// the home preview, and the upcoming-birthdays widgets.
//
// Notable additions in the calendar v3 rebuild:
//   • Recurrence expansion (event.recurrence) for daily / weekly (DOW list) /
//     bi-weekly / monthly-by-date / monthly-by-weekday / yearly.
//   • Birthday items derived from members.dob (month/day only, no year).
//   • Scope filter — "church" | "mine" | "both" — applied at build time.
//   • Color resolution: item.color > category default (kc_app_settings) > kind class.
import React from "react";
import { useAppSettings } from "../../lib/AppSettingsContext";

// Resolve the effective color for an item given app-level category defaults.
export function resolveItemColor(item, categoryColors) {
  if (!item) return "";
  if (item.color) return item.color;
  if (categoryColors && categoryColors[item.kind]) return categoryColors[item.kind];
  return "";
}

export function ymdKey(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}

export function sameYMD(a, b) {
  return ymdKey(a) === ymdKey(b);
}

export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function startOfWeek(d) {
  // Sunday-based week (matches MonthGrid header order S M T W T F S)
  const x = startOfDay(d);
  const dow = x.getDay();
  x.setDate(x.getDate() - dow);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

// Parse a YYYY-MM-DD date string as LOCAL midnight (no UTC shift).
// Mirrors the parseDateOnly helper in /lib/format.js so birthday/anniv dates
// don't drift by one day in negative-UTC viewers.
function _parseLocal(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  const s = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// Expand a service "day of week" string (e.g. "Sunday") into occurrences in a window.
export function expandServiceOccurrences(svc, fromDate, toDate) {
  const dayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
  const dow = (typeof svc.dayOfWeek === "number")
    ? svc.dayOfWeek
    : dayMap[(svc.day || svc.schedule || "").toLowerCase().trim()];
  if (svc.type === "one-time" || svc.type === "other") {
    if (svc.eventDate) {
      const d = _parseLocal(svc.eventDate);
      if (d && d >= startOfDay(fromDate) && d <= endOfDay(toDate)) return [d];
    }
    return [];
  }
  if (dow === undefined) return [];
  const occ = [];
  const cur = startOfDay(fromDate);
  const end = endOfDay(toDate);
  while (cur <= end) {
    if (cur.getDay() === dow) occ.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return occ;
}

// Same nth-weekday-of-month as the given date (e.g. 2nd Tuesday).
function _nthWeekdayOfMonth(date) {
  return Math.floor((date.getDate() - 1) / 7) + 1; // 1-based
}

// Expand a single event into all of its occurrences within [from, to].
// event.recurrence = {
//   type: "none" | "daily" | "weekly" | "biweekly" | "monthly-date" |
//         "monthly-weekday" | "yearly",
//   daysOfWeek?: [0..6],     // for "weekly"
//   until?: "YYYY-MM-DD",    // optional end date
//   exceptions?: ["YYYY-MM-DD", ...],  // single occurrences to skip
// }
export function expandEventOccurrences(ev, fromDate, toDate) {
  if (!ev || !ev.date) return [];
  const base = _parseLocal(ev.date);
  if (!base) return [];
  const from = startOfDay(fromDate);
  const to = endOfDay(toDate);
  const rec = ev.recurrence || { type: "none" };
  const until = rec.until ? endOfDay(_parseLocal(rec.until)) : null;
  const exceptions = new Set(Array.isArray(rec.exceptions) ? rec.exceptions : []);
  const out = [];
  const push = (d) => {
    if (exceptions.has(ymdKey(d))) return;
    if (until && d > until) return;
    if (d < from || d > to) return;
    out.push(new Date(d));
  };

  const type = rec.type || "none";
  if (type === "none") {
    push(base);
    return out;
  }
  if (type === "daily") {
    const cur = new Date(Math.max(from.getTime(), startOfDay(base).getTime()));
    const stop = until ? new Date(Math.min(to.getTime(), until.getTime())) : to;
    while (cur <= stop) { if (cur >= startOfDay(base)) push(cur); cur.setDate(cur.getDate() + 1); }
    return out;
  }
  if (type === "weekly" || type === "biweekly") {
    const days = Array.isArray(rec.daysOfWeek) && rec.daysOfWeek.length ? rec.daysOfWeek.slice() : [base.getDay()];
    const step = type === "biweekly" ? 14 : 7;
    // Anchor the cycle to the base date's week.
    const baseWeek = startOfWeek(base);
    const cur = new Date(Math.max(from.getTime(), baseWeek.getTime()));
    const cycleStart = startOfWeek(cur);
    // For bi-weekly: ensure the week aligns with the base anchor (every other week).
    const weeksFromBase = Math.floor((cycleStart - baseWeek) / (7 * 24 * 3600 * 1000));
    let weekParityOk = type === "biweekly" ? weeksFromBase % 2 === 0 : true;
    let weekStart = new Date(cycleStart);
    const stop = until ? new Date(Math.min(to.getTime(), until.getTime())) : to;
    while (weekStart <= stop) {
      if (weekParityOk) {
        for (const dow of days) {
          const d = new Date(weekStart); d.setDate(d.getDate() + dow);
          if (d >= startOfDay(base)) push(d);
        }
      }
      weekStart.setDate(weekStart.getDate() + step);
      // For weekly the step IS one week so parity stays true; for biweekly we already advanced 2 weeks.
      if (type === "biweekly") weekParityOk = true;
    }
    return out;
  }
  if (type === "monthly-date") {
    const cur = new Date(base.getFullYear(), base.getMonth(), 1);
    const stop = until ? new Date(Math.min(to.getTime(), until.getTime())) : to;
    while (cur <= stop) {
      // Skip months where the day doesn't exist (e.g. Feb 30).
      const last = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
      if (last >= base.getDate()) {
        const d = new Date(cur.getFullYear(), cur.getMonth(), base.getDate());
        if (d >= startOfDay(base)) push(d);
      }
      cur.setMonth(cur.getMonth() + 1);
    }
    return out;
  }
  if (type === "monthly-weekday") {
    const targetDow = base.getDay();
    const targetN = _nthWeekdayOfMonth(base);
    const cur = new Date(base.getFullYear(), base.getMonth(), 1);
    const stop = until ? new Date(Math.min(to.getTime(), until.getTime())) : to;
    while (cur <= stop) {
      // Find nth weekday of this month
      const first = new Date(cur.getFullYear(), cur.getMonth(), 1);
      const offset = (targetDow - first.getDay() + 7) % 7;
      const dayNum = 1 + offset + (targetN - 1) * 7;
      const last = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
      if (dayNum <= last) {
        const d = new Date(cur.getFullYear(), cur.getMonth(), dayNum);
        if (d >= startOfDay(base)) push(d);
      }
      cur.setMonth(cur.getMonth() + 1);
    }
    return out;
  }
  if (type === "yearly") {
    const yStart = from.getFullYear();
    const yEnd = (until ? until : to).getFullYear();
    for (let y = yStart; y <= yEnd; y++) {
      const d = new Date(y, base.getMonth(), base.getDate());
      if (d >= startOfDay(base)) push(d);
    }
    return out;
  }
  return out;
}

// Birthday items — one per member per year within [from, to]. Year of birth
// is hidden by spec; the item carries `meta` with "Birthday".
export function buildBirthdayItems(members, fromDate, toDate, currentUser, scope) {
  if (!Array.isArray(members) || members.length === 0) return [];
  const from = startOfDay(fromDate);
  const to = endOfDay(toDate);
  const out = [];
  const wantOnlyMine = scope === "mine";
  const userMemberId = currentUser ? currentUser.memberId : null;
  for (const m of members) {
    if (!m || !m.dob) continue;
    const dob = _parseLocal(m.dob);
    if (!dob) continue;
    if (wantOnlyMine && m.id !== userMemberId) continue;
    const yStart = from.getFullYear();
    const yEnd = to.getFullYear();
    for (let y = yStart; y <= yEnd; y++) {
      const d = new Date(y, dob.getMonth(), dob.getDate());
      if (d < from || d > to) continue;
      out.push({
        date: d,
        item: {
          kind: "birthday",
          id: `bday-${m.id}-${y}`,
          memberId: m.id,
          title: `${m.firstName || ""} ${m.lastName || ""}`.trim(),
          meta: "Birthday",
          allDay: true,
        },
      });
    }
  }
  return out;
}

// Build items for a date range with scope filter.
//   scope = "church" | "mine" | "both"
// - church → events, services, blockouts that apply to "all", birthdays (everyone)
// - mine   → my appointments, events I'm authority on / registered for, services
//            I'm authority on, my blockouts, my own birthday
// - both   → union of the two
export function buildItemsForRange({ from, to, currentUser, scope = "both", events, services, appointments, blockouts, members }) {
  const map = {};
  const add = (date, item) => {
    if (!date) return;
    const d = new Date(date);
    if (d < startOfDay(from) || d > endOfDay(to)) return;
    const k = ymdKey(d);
    (map[k] = map[k] || []).push(item);
  };

  const wantChurch = scope === "church" || scope === "both";
  const wantMine = scope === "mine" || scope === "both";

  // Events — with recurrence expansion. Church-wide unless tagged personal.
  (events || []).forEach(ev => {
    if (!ev || !ev.date) return;
    const isMineLinked = Array.isArray(ev.authorityIds) && currentUser && ev.authorityIds.includes(currentUser.id);
    const isRegistered = Array.isArray(ev.registrations) && currentUser && currentUser.memberId && ev.registrations.includes(currentUser.memberId);
    // All church events show in Church and Both. Events the user is linked to also show in Mine.
    const showChurch = wantChurch;
    const showMine = wantMine && (isMineLinked || isRegistered);
    if (!showChurch && !showMine) return;
    const occurrences = expandEventOccurrences(ev, from, to);
    occurrences.forEach(d => add(d, {
      kind: "event",
      id: ev.id,
      title: ev.name,
      meta: ev.location || (ev.recurrence && ev.recurrence.type && ev.recurrence.type !== "none" ? "Recurring" : "Event"),
      time: ev.time || null,
      color: ev.color || "",
      date: ymdKey(d),
      ref: ev,
      isMine: showMine,
    }));
  });

  // Services — recurring or one-time. Church-wide.
  (services || []).forEach(s => {
    if (!s) return;
    const isAuthority = Array.isArray(s.authorityIds) && currentUser && s.authorityIds.includes(currentUser.id);
    const showChurch = wantChurch;
    const showMine = wantMine && isAuthority;
    if (!showChurch && !showMine) return;
    const occs = expandServiceOccurrences(s, from, to);
    occs.forEach(d => add(d, {
      kind: "service",
      id: s.id,
      title: s.name,
      meta: `${s.day || (typeof s.dayOfWeek === "number" ? ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][s.dayOfWeek] : "")}${s.time ? ` · ${s.time}` : ""}`.trim() || "Service",
      time: s.time || null,
      color: s.color || "",
      date: ymdKey(d),
      ref: s,
      isMine: showMine,
    }));
  });

  // Appointments — strictly personal.
  if (wantMine) {
    (appointments || []).forEach(a => {
      if (!a || !currentUser) return;
      if (a.userId !== currentUser.id) return;
      if (!a.date) return;
      const d = _parseLocal(a.date) || new Date(a.date);
      add(d, {
        kind: "appointment", id: a.id, title: a.title || "Appointment",
        meta: a.with || "", time: a.time || null, date: ymdKey(d), ref: a, isMine: true,
      });
    });
  }

  // Blockouts — affects users in `appliesTo` (or "all").
  const userIsAffected = (b) => {
    if (!b || !currentUser) return false;
    const ap = b.appliesTo;
    if (ap === "all" || (Array.isArray(ap) && ap.includes("all"))) return wantChurch; // all-staff
    return Array.isArray(ap) && ap.includes(currentUser.id) && wantMine;
  };
  (blockouts || []).filter(userIsAffected).forEach(b => {
    if (!b.startDate) return;
    const s = startOfDay(_parseLocal(b.startDate) || new Date(b.startDate));
    const e = b.endDate ? startOfDay(_parseLocal(b.endDate) || new Date(b.endDate)) : s;
    const cur = new Date(s);
    const ap = b.appliesTo;
    const isMineBlock = Array.isArray(ap) && currentUser && ap.includes(currentUser.id);
    while (cur <= e) {
      add(cur, {
        kind: "blockout",
        id: b.id,
        title: b.name || "Blockout",
        meta: b.allDay ? "All day" : "Blockout",
        date: ymdKey(cur),
        ref: b,
        isMine: !!isMineBlock,
      });
      cur.setDate(cur.getDate() + 1);
    }
  });

  // Birthdays — year-stripped, scoped.
  if (Array.isArray(members)) {
    const bScope = wantMine && !wantChurch ? "mine" : "all";
    const bdays = buildBirthdayItems(members, from, to, currentUser, bScope);
    bdays.forEach(({ date, item }) => add(date, { ...item, date: ymdKey(date), isMine: item.memberId === (currentUser && currentUser.memberId) }));
  }

  // Sort each day's items by time ascending so chronological order is intuitive.
  Object.keys(map).forEach(k => {
    map[k].sort((a, b) => {
      const ta = a.time || (a.allDay ? "" : "99:99");
      const tb = b.time || (b.allDay ? "" : "99:99");
      return String(ta).localeCompare(String(tb));
    });
  });
  return { itemsByDate: map };
}

// Legacy alias for the month-view callsites that pass year/month.
export function buildCalendarItems({ year, month, currentUser, scope = "both", events, services, appointments, blockouts, members }) {
  const from = new Date(year, month, 1);
  const to = new Date(year, month + 1, 0); to.setHours(23, 59, 59, 999);
  return buildItemsForRange({ from, to, currentUser, scope, events, services, appointments, blockouts, members });
}

// Reusable MonthGrid component
export function MonthGrid({ year, month, itemsByDate, selectedDate, onSelectDate, compact, today, maxDots = 3 }) {
  const { settings } = useAppSettings();
  const categoryColors = (settings && settings.calendarCategoryColors) || {};
  const firstDayOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDow = firstDayOfMonth.getDay();
  const todayDate = today || new Date();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const k = ymdKey(date);
    const items = itemsByDate[k] || [];
    const isToday = sameYMD(date, todayDate);
    const isSelected = selectedDate && sameYMD(date, selectedDate);
    cells.push({ d, date, items, isToday, isSelected });
  }
  return (
    <div className="cal-grid" data-testid="cal-grid">
      {["S", "M", "T", "W", "T", "F", "S"].map((w, i) => (
        <div key={`w${i}`} className="cal-weekday">{w}</div>
      ))}
      {cells.map((c, i) => {
        if (!c) return <div key={`e${i}`} className="cal-day empty" />;
        const overflow = c.items.length > maxDots;
        return (
          <button
            type="button"
            key={`d${c.d}`}
            data-testid={`cal-day-${c.d}`}
            className={`cal-day ${c.isToday ? "is-today" : ""} ${c.isSelected ? "is-selected" : ""} ${c.items.length > 0 ? "has-items" : ""}`}
            onClick={() => onSelectDate && onSelectDate(c.date, c.items)}
            aria-label={`${c.date.toDateString()}${c.items.length ? ` — ${c.items.length} item${c.items.length === 1 ? "" : "s"}` : ""}`}
          >
            <span className="cal-day-num">{c.d}</span>
            {c.items.length > 0 && (
              <div className="cal-dots" aria-hidden="true">
                {c.items.slice(0, compact ? 1 : maxDots).map((it, idx) => {
                  const eff = resolveItemColor(it, categoryColors);
                  const inline = eff ? { background: eff, borderColor: eff } : undefined;
                  return <span key={idx} className={`cal-dot kind-${it.kind}`} style={inline} />;
                })}
                {overflow && !compact && <span className="cal-dot-overflow">+{c.items.length - maxDots}</span>}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
